// components
import { Button } from '@mui/material';
import { Dialog } from 'components/shared/index';
import { OverlapAction, RecurrenceAction } from './components';
import LoadingIcon from 'components/shared/LoadingIcon/LoadingIcon';

// hooks
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppointmentMutation } from './SmartActionDialog.hooks';

// styles
import { ActionsContainer } from '../styles';
import { ContentContainer } from './SmartActionDialog.styles';

// types
import {
  SmartActionDialogProps,
  SmartAction,
  SmartActionState,
  SmartActionType
} from './SmartActionDialog.types';
import {
  AppointmentFormFieldData,
  AppointmentRecurrenceType
} from 'components/mrp/components/AppointmentForm/AppointmentForm.types';
import { RecurrenceExceptionUpdateType } from 'generated/index';

export const SmartActionDialog = ({
  actionType = 'update',
  open,
  onClose,
  onOpen,
  onExecuteAction,
  payload = {} as AppointmentFormFieldData
}: SmartActionDialogProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'mrp' });

  const { execute, isLoading } = useAppointmentMutation();

  const [actions, setActions] = useState<SmartAction[]>([
    {
      type: SmartActionType.RECURRENCE_ACTION,
      state: SmartActionState.NONE
    },
    {
      type: SmartActionType.OVERLAP_ACTION,
      state: SmartActionState.NONE,
      interactionType: 'informational'
    }
  ]);

  const [canContinue, setCanContinue] = useState(false);

  const [selectedValues, setSelectedValues] = useState<{
    editAppointmentType: AppointmentRecurrenceType | undefined;
  }>({
    editAppointmentType: undefined
  });

  const isSeriesInstance = payload.appointmentType === 'SERIES_INSTANCE';
  const isSeriesException = payload.appointmentType === 'SERIES_EXCEPTION';
  const isPartOfSeries = isSeriesInstance || isSeriesException;
  const isCopyPasteAction = !!payload.isCopyPasteAction;

  const enabledActions = actions.filter((action) => action.enabled !== false);
  const currentAction =
    enabledActions.find((action) => action.state === SmartActionState.ACTIVE) ||
    enabledActions[0];
  const isLastAction =
    enabledActions.find(({ state }) => state === SmartActionState.ACTIVE)
      ?.type === currentAction.type;

  const skipActions =
    actions.length === 0 ||
    !isPartOfSeries ||
    (isSeriesException && isCopyPasteAction) ||
    !!payload.editAppointmentType ||
    enabledActions.every((action) => action.props?.messages.length > 0);
  const hasNotifications = actions.some(
    (action) => action.props?.messages?.length > 0
  );
  const showLoaderOnly = skipActions && !hasNotifications;

  const recurrenceAction = actions.find(
    (action) => action.type === SmartActionType.RECURRENCE_ACTION
  );
  const overlapAction = actions.find(
    (action) => action.type === SmartActionType.OVERLAP_ACTION
  );

  const handleCloseAndFinish = () => {
    onClose?.({ ok: true });
  };

  const handleResponse = <TResponse,>(response: TResponse | any) => {
    // If messages are present (= overlap), inject the messages into the overlap action
    if (response?.success === false && response?.notifications?.length > 0) {
      const messages: string[] = response.notifications.map(
        ({ text }) => `• ${text}`
      );

      setActions((prev) => {
        return prev.map((action) => {
          if (action.type === SmartActionType.OVERLAP_ACTION) {
            // No action is needed for this step, so we can allow
            // to continue immediately.
            setCanContinue(true);

            return {
              ...action,
              props: { messages },
              state: SmartActionState.ACTIVE
            };
          }
          return action;
        });
      });
    }

    onExecuteAction?.(currentAction.type, response);
  };

  const handleNextAction = async (
    props?: Record<string, any> & { type: SmartActionType }
  ) => {
    setCanContinue(false);

    const nextAction = enabledActions.find(
      (action) => action.state === SmartActionState.NONE
    );

    const updatedPayload = {
      ...payload,
      editAppointmentType:
        selectedValues?.editAppointmentType ||
        payload.editAppointmentType ||
        (actionType === 'create'
          ? undefined
          : AppointmentRecurrenceType.ONLY_THIS), // Don't use fallback if action = create
      recurrenceExceptionUpdateType:
        (selectedValues?.editAppointmentType || payload.editAppointmentType) ===
        AppointmentRecurrenceType.THIS_AND_ALL_SUBSEQUENT
          ? 'UNION'
          : 'EXCLUDE'
    } satisfies AppointmentFormFieldData & {
      recurrenceExceptionUpdateType?: RecurrenceExceptionUpdateType;
    };

    try {
      let response: Awaited<ReturnType<typeof execute>>;

      response = await execute(
        updatedPayload,
        actionType,
        currentAction.type === SmartActionType.OVERLAP_ACTION
      );
      handleResponse(response);

      if (skipActions && (response?.notifications || []).length === 0)
        handleCloseAndFinish();

      setActions((prev = []) => {
        const currentAction =
          prev.find(
            (action) =>
              action.state === SmartActionState.ERROR ||
              action.state === SmartActionState.ACTIVE
          ) || enabledActions[0];

        return prev.map((action) => {
          // Consider the current step finished
          if (action.type === currentAction.type) {
            return {
              ...action,
              state:
                action.type === SmartActionType.RECURRENCE_ACTION &&
                !isPartOfSeries
                  ? SmartActionState.NONE
                  : SmartActionState.SUCCESS
            };
          }

          if (action.type === nextAction?.type) {
            if (
              nextAction.type === SmartActionType.OVERLAP_ACTION &&
              (response?.notifications || [])?.length === 0
            ) {
              // There are no actions left, so we close the dialog
              handleCloseAndFinish();

              return {
                ...action,
                enabled: false
              };
            }

            // Activate the next step
            return {
              ...action,
              state: SmartActionState.ACTIVE
            };
          }

          return action;
        });
      });

      setSelectedValues({
        editAppointmentType:
          props?.editAppointmentType || selectedValues.editAppointmentType
      });

      !nextAction && handleCloseAndFinish();
    } catch {
      setActions((prev) => {
        return prev.map((action) => {
          if (action.type === currentAction.type) {
            return {
              ...action,
              state: SmartActionState.ERROR
            };
          }
          return action;
        });
      });
    }
  };

  const renderContent = () => {
    return (
      <ContentContainer>
        {!payload.editAppointmentType && (
          <RecurrenceAction
            state={recurrenceAction?.state}
            onSelect={nextActionOrNone}
            setCanContinue={setCanContinue}
          />
        )}

        <OverlapAction
          state={overlapAction?.state}
          messages={overlapAction?.props?.messages as string[]}
          onSelect={nextActionOrNone}
          setCanContinue={setCanContinue}
        />

        <ActionsContainer>
          <Button
            variant="text"
            fullWidth
            onClick={onClose?.bind(null, undefined)}
          >
            {t('dialogs.smartAction.cancel')}
          </Button>

          <Button
            disabled={
              !canContinue ||
              isLoading ||
              (currentAction.state === SmartActionState.ACTIVE &&
                currentAction.interactionType !== 'informational' &&
                !isLastAction)
            }
            fullWidth
            onClick={handleNextAction.bind(null, undefined)}
          >
            {isLoading ? (
              <LoadingIcon size="SMALL" />
            ) : (
              t('dialogs.smartAction.confirm')
            )}
          </Button>
        </ActionsContainer>
      </ContentContainer>
    );
  };

  const nextActionOrNone = (props) => {
    // The last action must be confirmed manually, but in case it is the last action,
    // we must still store the values returned by the handler
    if (isLastAction) {
      setSelectedValues((prev) => ({
        ...prev,
        editAppointmentType:
          props?.editAppointmentType || prev.editAppointmentType
      }));

      return () => null;
    }
    return handleNextAction;
  };

  useEffect(() => {
    open && onOpen?.();
  }, [open]);

  useEffect(() => {
    // If we have no actions to perform, we simply send the payload
    if (skipActions) handleNextAction();
  }, [skipActions]);

  useEffect(() => {
    setActions((prev) => {
      // Activate the first action
      return prev.map((action, index) => ({
        ...action,
        state: index === 0 ? SmartActionState.ACTIVE : SmartActionState.NONE
      }));
    });
  }, []);

  return (
    <Dialog
      handleClose={onClose?.bind(null, undefined)}
      open={open}
      title={t('dialogs.smartAction.title')}
      hideTitle={showLoaderOnly}
    >
      {showLoaderOnly ? <LoadingIcon /> : renderContent()}
    </Dialog>
  );
};
